import { Overlay } from '@angular/cdk/overlay';
import { HttpClientModule } from '@angular/common/http';
import { ErrorHandler, ExistingProvider, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MODAL_WIDTHS_CONFIG, impIconsConfig } from '@improver/app-config';
import { DATEPICKER_PLACEHOLDER_CONFIG, IMP_DATEPICKER_SCROLL_STRATEGY } from '@improver/components/datepicker';
import {
  IMP_DAYJS_DATETIME_ADAPTER_OPTIONS,
  IMP_DAYJS_DATETIME_ADAPTER_TRANSLATIONS,
  ImpDayjsDatetimeModule,
} from '@improver/components/dayjs-datetime';
import { IconsConfigInitializerModule } from '@improver/components/icons';
import { NgxTranslateAdapterModule } from '@improver/components/ngx-translate-adapter';
import { ResponsiveService } from '@improver/infrastructure/layout-data-layer';
import { BaseNavigationService, HISTORY_NAVIGATION_PROVIDER_TOKEN } from '@improver/infrastructure/navigation-data-layer';
import { InfrastructureSentryModule } from '@improver/infrastructure/sentry-data-layer';
import { ImproverComponentLibraryModule } from '@improver/shared-ui/component-library';
import { ImproverContentLayoutModule } from '@improver/shared-ui/layout';
import { TranslateService } from '@ngx-translate/core';
import { NgProgress } from 'ngx-progressbar';
import { GlobalErrorHandler } from './errors/global-error-handler.service';
import { HttpInterceptorsModule } from './interceptors/http-interceptors.module';
import { TranslationAdapter } from './providers/translation/translation-adapter';

export const BASE_NAVIGATION_SERVICE_PROVIDER: ExistingProvider = {
  provide: HISTORY_NAVIGATION_PROVIDER_TOKEN,
  useExisting: BaseNavigationService,
};

@NgModule({
  imports: [
    BrowserModule,

    HttpClientModule,
    HttpInterceptorsModule.forRoot(),
    ImproverContentLayoutModule.forRoot({ baseNavigationProvider: BASE_NAVIGATION_SERVICE_PROVIDER }),
    IconsConfigInitializerModule.forRoot(impIconsConfig),
    ImpDayjsDatetimeModule,
    NgxTranslateAdapterModule,
    InfrastructureSentryModule.forRoot(),
    ImproverComponentLibraryModule.forRoot({
      modalWithConfig: MODAL_WIDTHS_CONFIG,
    }),
  ],
  providers: [
    provideAnimationsAsync(),
    NgProgress,
    {
      provide: DATEPICKER_PLACEHOLDER_CONFIG,
      useFactory: (translateService: TranslateService) => ({
        DD: translateService.instant('APP_DATEPICKER_PLACEHOLDER_DAYS_SHORT'),
        MM: translateService.instant('APP_DATEPICKER_PLACEHOLDER_MONTHS_SHORT'),
        YYYY: translateService.instant('APP_DATEPICKER_PLACEHOLDER_YEARS_SHORT'),
      }),
      deps: [TranslateService],
    },
    {
      provide: IMP_DATEPICKER_SCROLL_STRATEGY,
      useFactory: (responsiveService: ResponsiveService, overlay: Overlay) => {
        const mobile = responsiveService.isMobile();

        return mobile ? () => overlay.scrollStrategies.reposition() : () => overlay.scrollStrategies.close();
      },
      deps: [ResponsiveService, Overlay],
    },
    {
      provide: IMP_DAYJS_DATETIME_ADAPTER_OPTIONS,
      useValue: {
        useUtc: false,
      },
    },
    {
      provide: IMP_DAYJS_DATETIME_ADAPTER_TRANSLATIONS,
      useClass: TranslationAdapter,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
})
export class CoreModule {}
